import { useState, useEffect, useCallback, useRef, type FormEvent, type ChangeEvent } from 'react';
import type { ExpenseRecord, ExpensePaymentMethod, ExpenseStatus } from '../../services/expenseApi';
import {
    fetchExpenses,
    createExpense,
    updateExpense,
    deleteExpense,
} from '../../services/expenseApi';
import type { ExpenseCategoryRecord } from '../../services/expenseCategoryApi';
import { fetchExpenseCategories } from '../../services/expenseCategoryApi';
import type { ApiError } from '../../services/api';
import { useCurrentSchoolName } from '../../hooks/useCurrentSchoolName';
import { numberToWords } from './expenseMockData';
import './ExpenseLedger.css';
import { useTranslation } from 'react-i18next';

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_LABELS: Record<ExpenseStatus, string> = {
    paid: 'Paid', pending: 'Pending', rejected: 'Rejected', void: 'Void',
};

const PAYMENT_METHODS: { id: ExpensePaymentMethod; label: string }[] = [
    { id: 'cash', label: 'Cash' },
    { id: 'cheque', label: 'Cheque' },
    { id: 'bank_transfer', label: 'Bank Transfer' },
];

const ACCEPTED_FILE_TYPES = '.jpeg,.jpg,.png,.webp,.gif,.pdf';
const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

type DrawerMode = 'closed' | 'create' | 'edit';

/* ── Voucher Print ── */
function openVoucher(exp: ExpenseRecord, schoolName?: string | null) {
    const dateStr = formatDate(exp.expense_date);
    const amount = parseFloat(exp.amount);
    const amtWords = numberToWords(amount);
    const method = exp.payment_method
        ? exp.payment_method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        : '—';
    const categoryName = exp.category?.name ?? '—';

    const html = `<!DOCTYPE html>
<html>
<head>
<title>Voucher — ${exp.voucher_number ?? exp.id.slice(0, 8)}</title>
<style>
  @page { size: A5 landscape; margin: 10mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1e293b; padding: 24px; max-width: 600px; margin: 0 auto; }
  .header { text-align: center; border-bottom: 2px solid #1e293b; padding-bottom: 14px; margin-bottom: 16px; }
  .header h1 { font-size: 18px; font-weight: 800; }
  .header p.sub { font-size: 10px; color: #6b7280; margin-top: 2px; }
  .header h2 { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-top: 10px; color: #374151; }
  .meta { display: flex; justify-content: space-between; margin-bottom: 18px; font-size: 12px; }
  .meta .label { color: #6b7280; }
  .meta .value { font-weight: 600; }
  .detail { margin-bottom: 16px; font-size: 12px; }
  .detail .row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #f3f4f6; }
  .detail .row:last-child { border-bottom: none; }
  .detail .label { color: #6b7280; }
  .detail .value { font-weight: 600; }
  .amount-box { text-align: center; margin: 20px 0; padding: 14px; border: 2px solid #1e293b; border-radius: 6px; }
  .amount-box .num { font-size: 26px; font-weight: 800; font-variant-numeric: tabular-nums; }
  .amount-box .words { font-size: 11px; color: #4b5563; margin-top: 4px; font-style: italic; }
  .signatures { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 12px; }
  .sig { text-align: center; width: 30%; }
  .sig .line { border-top: 1px solid #9ca3af; margin-bottom: 4px; }
  .sig .title { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <div class="header">
    <h1>${schoolName || 'Gurukul Academy'}</h1>
    <p class="sub">An Institution of Excellence</p>
    <h2>Payment Voucher</h2>
  </div>

  <div class="meta">
    <div><span class="label">Voucher No: </span><span class="value">${exp.voucher_number ?? '—'}</span></div>
    <div><span class="label">Date: </span><span class="value">${dateStr}</span></div>
  </div>

  <div class="detail">
    <div class="row"><span class="label">Title</span><span class="value">${exp.title}</span></div>
    <div class="row"><span class="label">Category</span><span class="value">${categoryName}</span></div>
    <div class="row"><span class="label">Description</span><span class="value">${exp.description ?? '—'}</span></div>
    <div class="row"><span class="label">Payment Method</span><span class="value">${method}</span></div>
    ${exp.reference_number ? `<div class="row"><span class="label">Reference No.</span><span class="value">${exp.reference_number}</span></div>` : ''}
  </div>

  <div class="amount-box">
    <div class="num">${formatCurrency(amount)}</div>
    <div class="words">${amtWords}</div>
  </div>

  <div class="signatures">
    <div class="sig"><div class="line"></div><div class="title">Prepared By</div></div>
    <div class="sig"><div class="line"></div><div class="title">Authorized By</div></div>
    <div class="sig"><div class="line"></div><div class="title">Receiver's Signature</div></div>
  </div>
</body>
<script>window.onload=function(){window.print();}<\\/script>
</html>`;

    const w = window.open('', '_blank', 'width=700,height=500');
    if (w) { w.document.write(html); w.document.close(); }
}

/* ════════════════════════════════════════════════════ */
export default function ExpenseLedger() {
    const { t: _t } = useTranslation();
    const schoolName = useCurrentSchoolName();
    const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
    const [categories, setCategories] = useState<ExpenseCategoryRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [saving, setSaving] = useState(false);

    /* Filters */
    const [filterCategory, setFilterCategory] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    /* Drawer */
    const [drawerMode, setDrawerMode] = useState<DrawerMode>('closed');
    const [editId, setEditId] = useState<string | null>(null);
    const [fDate, setFDate] = useState(new Date().toISOString().slice(0, 10));
    const [fCategory, setFCategory] = useState('');
    const [fTitle, setFTitle] = useState('');
    const [fDesc, setFDesc] = useState('');
    const [fAmount, setFAmount] = useState<number | ''>('');
    const [fMethod, setFMethod] = useState<ExpensePaymentMethod>('cash');
    const [fRef, setFRef] = useState('');
    const [fStatus, setFStatus] = useState<'pending' | 'paid'>('pending');
    const [fReceipts, setFReceipts] = useState<File[]>([]);
    const [fErrors, setFErrors] = useState<Record<string, string>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [deleteTarget, setDeleteTarget] = useState<ExpenseRecord | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setErrorMsg('');
        try {
            const [expRes, cats] = await Promise.all([
                fetchExpenses({ per_page: 100 }),
                fetchExpenseCategories(),
            ]);
            setExpenses(expRes.data);
            setCategories(cats);
        } catch {
            setErrorMsg('Failed to load expenses.');
            setExpenses([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const showSuccess = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    /* ── Drawer controls ── */
    const openCreate = () => {
        setDrawerMode('create'); setEditId(null);
        setFDate(new Date().toISOString().slice(0, 10));
        setFCategory(categories.length > 0 ? categories[0].id : '');
        setFTitle(''); setFDesc(''); setFAmount(''); setFMethod('cash'); setFRef('');
        setFStatus('pending'); setFReceipts([]);
        setFErrors({}); setErrorMsg('');
    };

    const openEdit = (exp: ExpenseRecord) => {
        setDrawerMode('edit'); setEditId(exp.id);
        setFDate(exp.expense_date);
        setFCategory(exp.expense_category_id);
        setFTitle(exp.title);
        setFDesc(exp.description ?? '');
        setFAmount(parseFloat(exp.amount));
        setFMethod(exp.payment_method ?? 'cash');
        setFRef(exp.reference_number ?? '');
        setFStatus(exp.status === 'paid' ? 'paid' : 'pending');
        setFReceipts([]);
        setFErrors({}); setErrorMsg('');
    };

    const closeDrawer = () => { setDrawerMode('closed'); setEditId(null); };

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!fDate) errs.expense_date = 'Date is required.';
        if (!fCategory) errs.expense_category_id = 'Select a category.';
        if (!fTitle.trim()) errs.title = 'Title is required.';
        if (fTitle.trim().length > 150) errs.title = 'Title must be 150 characters or less.';
        if (fAmount === '' || Number(fAmount) < 0.01) errs.amount = 'Amount must be at least 0.01.';
        if (fRef.trim().length > 100) errs.reference_number = 'Reference must be 100 characters or less.';
        if (fReceipts.length > MAX_FILES) errs.receipts = `Maximum ${MAX_FILES} files allowed.`;
        for (const file of fReceipts) {
            if (file.size > MAX_FILE_SIZE) {
                errs.receipts = 'Each file must be under 10 MB.';
                break;
            }
        }
        setFErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleFileChange = (ev: ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files) return;
        const newFiles = Array.from(files);
        setFReceipts((prev) => {
            const combined = [...prev, ...newFiles];
            return combined.slice(0, MAX_FILES);
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index: number) => {
        setFReceipts((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        if (!validate()) return;

        const schoolId = localStorage.getItem('school_id');
        if (!schoolId) {
            setErrorMsg('No school selected. Please select a school first.');
            return;
        }

        setSaving(true);
        setErrorMsg('');
        try {
            if (drawerMode === 'create') {
                const created = await createExpense({
                    school_id: schoolId,
                    expense_category_id: fCategory,
                    title: fTitle.trim(),
                    description: fDesc.trim() || null,
                    amount: Number(fAmount),
                    expense_date: fDate,
                    status: fStatus,
                    payment_method: fMethod,
                    reference_number: fRef.trim() || null,
                    receipts: fReceipts.length > 0 ? fReceipts : undefined,
                });
                setExpenses((prev) => [created, ...prev]);
                showSuccess('Expense recorded.');
            } else if (drawerMode === 'edit' && editId) {
                const updated = await updateExpense(editId, {
                    expense_category_id: fCategory,
                    title: fTitle.trim(),
                    description: fDesc.trim() || null,
                    amount: Number(fAmount),
                    expense_date: fDate,
                    status: fStatus,
                    payment_method: fMethod,
                    reference_number: fRef.trim() || null,
                    receipts: fReceipts.length > 0 ? fReceipts : undefined,
                });
                setExpenses((prev) => prev.map((e) => e.id === editId ? updated : e));
                showSuccess('Expense updated.');
            }
            closeDrawer();
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const fieldErrors: Record<string, string> = {};
                for (const [key, msgs] of Object.entries(apiErr.errors)) {
                    fieldErrors[key] = msgs[0];
                }
                setFErrors(fieldErrors);
            } else {
                setErrorMsg(apiErr.message || 'Failed to save expense.');
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        setErrorMsg('');
        try {
            await deleteExpense(deleteTarget.id);
            setExpenses((prev) => prev.filter((e) => e.id !== deleteTarget.id));
            setDeleteTarget(null);
            showSuccess('Expense removed.');
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to delete expense.');
            setDeleteTarget(null);
        } finally {
            setSaving(false);
        }
    };

    /* ── Filtering ── */
    const filtered = (() => {
        const q = searchQuery.toLowerCase();
        return expenses.filter((e) => {
            if (filterCategory && e.expense_category_id !== filterCategory) return false;
            if (q && !e.title.toLowerCase().includes(q) && !(e.voucher_number ?? '').toLowerCase().includes(q) && !(e.description ?? '').toLowerCase().includes(q)) return false;
            return true;
        });
    })();

    /* ── Summary metrics ── */
    const totalExpenses = filtered.reduce((s, e) => s + parseFloat(e.amount), 0);
    const pendingPayments = filtered.filter((e) => e.status === 'pending').reduce((s, e) => s + parseFloat(e.amount), 0);
    const topCategory = (() => {
        const map = new Map<string, number>();
        filtered.forEach((e) => {
            const catName = e.category?.name ?? '—';
            map.set(catName, (map.get(catName) ?? 0) + parseFloat(e.amount));
        });
        let best = '—';
        let bestAmt = 0;
        map.forEach((amt, name) => { if (amt > bestAmt) { best = name; bestAmt = amt; } });
        return best;
    })();

    const needsRef = fMethod === 'cheque' || fMethod === 'bank_transfer';
    const drawerOpen = drawerMode !== 'closed';

    return (
        <div className="expl">
            {successMsg && (
                <div className="expl__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="expl__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {errorMsg && (
                <div className="expl__error-banner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                </div>
            )}

            <div className="expl__header">
                <div className="expl__header-text">
                    <h1 className="expl__title">Expense Ledger</h1>
                    <p className="expl__subtitle">Track and manage all outbound payments.</p>
                </div>
                <button className="expl__add-btn" onClick={openCreate}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    New Expense
                </button>
            </div>

            {/* ── Summary Cards ── */}
            <div className="expl__summary-row">
                <div className="expl__summary-card">
                    <span className="expl__summary-label">Total Expenses</span>
                    <span className="expl__summary-value">{formatCurrency(totalExpenses)}</span>
                </div>
                <div className="expl__summary-card expl__summary-card--warn">
                    <span className="expl__summary-label">Pending Payments</span>
                    <span className="expl__summary-value">{formatCurrency(pendingPayments)}</span>
                </div>
                <div className="expl__summary-card">
                    <span className="expl__summary-label">Top Category</span>
                    <span className="expl__summary-value expl__summary-value--text">{topCategory}</span>
                </div>
            </div>

            {/* ── Context Bar ── */}
            <div className="expl__context">
                <span className="expl__context-label">Category</span>
                <select className="expl__context-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    <option value="">All Categories</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                <div className="expl__context-divider" />

                <div className="expl__search-wrap">
                    <svg className="expl__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    <input className="expl__search-input" type="text" placeholder="Title, voucher…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>

                <span className="expl__context-info">{filtered.length} records</span>
            </div>

            {/* ── Data Grid ── */}
            {loading ? (
                <div className="expl__loading"><div className="expl__loader"><span className="expl__loader-dot" /><span className="expl__loader-dot" /><span className="expl__loader-dot" /></div></div>
            ) : filtered.length === 0 ? (
                <div className="expl__empty">
                    <h3 className="expl__empty-title">No expenses found</h3>
                    <p className="expl__empty-text">Try adjusting your filters or add a new expense.</p>
                </div>
            ) : (
                <div className="expl__table-wrap">
                    <table className="expl__table">
                        <thead>
                            <tr>
                                <th className="expl__th">Date</th>
                                <th className="expl__th">Voucher No.</th>
                                <th className="expl__th">Title</th>
                                <th className="expl__th">Category</th>
                                <th className="expl__th">Method</th>
                                <th className="expl__th expl__th--amount">Amount</th>
                                <th className="expl__th">Status</th>
                                <th className="expl__th expl__th--actions"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((e) => (
                                <tr className="expl__row" key={e.id}>
                                    <td className="expl__td expl__td--date">{formatDate(e.expense_date)}</td>
                                    <td className="expl__td expl__td--voucher">{e.voucher_number ?? '—'}</td>
                                    <td className="expl__td expl__td--payee">{e.title}</td>
                                    <td className="expl__td expl__td--cat">{e.category?.name ?? '—'}</td>
                                    <td className="expl__td">
                                        {e.payment_method ? (
                                            <span className="expl__method-badge">{e.payment_method.replace('_', ' ')}</span>
                                        ) : '—'}
                                    </td>
                                    <td className="expl__td expl__td--amount">{formatCurrency(parseFloat(e.amount))}</td>
                                    <td className="expl__td">
                                        <span className={`expl__status expl__status--${e.status}`}>{STATUS_LABELS[e.status]}</span>
                                    </td>
                                    <td className="expl__td expl__td--actions">
                                        <button className="expl__row-btn" title="Print Voucher" onClick={() => openVoucher(e, schoolName)}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                                        </button>
                                        <button className="expl__row-btn" title="Edit" onClick={() => openEdit(e)}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                        </button>
                                        <button className="expl__row-btn expl__row-btn--delete" title="Delete" onClick={() => setDeleteTarget(e)}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Drawer ── */}
            {drawerOpen && <div className="expl__drawer-backdrop" onClick={closeDrawer} />}
            <div className={`expl__drawer ${drawerOpen ? 'expl__drawer--open' : ''}`}>
                <form className="expl__drawer-inner" onSubmit={handleSubmit}>
                    <div className="expl__drawer-header">
                        <h2 className="expl__drawer-title">{drawerMode === 'create' ? 'New Expense' : 'Edit Expense'}</h2>
                        <button className="expl__drawer-close" type="button" onClick={closeDrawer}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>

                    <div className="expl__drawer-body">
                        {/* Card 1 — Basic Details */}
                        <div className="expl__drawer-card">
                            <h4 className="expl__card-title">Basic Details</h4>
                            <div className="expl__field">
                                <label className="expl__label">Date *</label>
                                <input className={`expl__input ${fErrors.expense_date ? 'expl__input--error' : ''}`} type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} />
                                {fErrors.expense_date && <p className="expl__error">{fErrors.expense_date}</p>}
                            </div>
                            <div className="expl__field">
                                <label className="expl__label">Category *</label>
                                <select className={`expl__select ${fErrors.expense_category_id ? 'expl__input--error' : ''}`} value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                                    <option value="">Select Category</option>
                                    {categories.filter((c) => c.is_active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                {fErrors.expense_category_id && <p className="expl__error">{fErrors.expense_category_id}</p>}
                            </div>
                            <div className="expl__field">
                                <label className="expl__label">Title *</label>
                                <input className={`expl__input ${fErrors.title ? 'expl__input--error' : ''}`} type="text" value={fTitle} onChange={(e) => setFTitle(e.target.value)} placeholder="e.g. February electricity bill" maxLength={150} />
                                {fErrors.title && <p className="expl__error">{fErrors.title}</p>}
                            </div>
                            <div className="expl__field">
                                <label className="expl__label">Description</label>
                                <textarea className={`expl__textarea ${fErrors.description ? 'expl__input--error' : ''}`} value={fDesc} onChange={(e) => setFDesc(e.target.value)} placeholder="Additional details…" rows={2} />
                                {fErrors.description && <p className="expl__error">{fErrors.description}</p>}
                            </div>
                        </div>

                        {/* Card 2 — Financials */}
                        <div className="expl__drawer-card">
                            <h4 className="expl__card-title">Financials</h4>
                            <div className="expl__field">
                                <label className="expl__label">Amount *</label>
                                <div className="expl__amount-wrap">
                                    <span className="expl__amount-symbol">₹</span>
                                    <input className={`expl__amount-input ${fErrors.amount ? 'expl__input--error' : ''}`} type="number" min={0.01} step="0.01" value={fAmount} onChange={(e) => setFAmount(e.target.value === '' ? '' : Number(e.target.value))} placeholder="0.00" />
                                </div>
                                {fErrors.amount && <p className="expl__error">{fErrors.amount}</p>}
                            </div>
                            <div className="expl__field">
                                <label className="expl__label">Payment Method</label>
                                <div className="expl__method-grid">
                                    {PAYMENT_METHODS.map((m) => (
                                        <button key={m.id} type="button" className={`expl__method-btn ${fMethod === m.id ? 'expl__method-btn--active' : ''}`} onClick={() => setFMethod(m.id)}>
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {needsRef && (
                                <div className="expl__field expl__field--slide">
                                    <label className="expl__label">{fMethod === 'cheque' ? 'Cheque No.' : 'Reference No.'}</label>
                                    <input className={`expl__input ${fErrors.reference_number ? 'expl__input--error' : ''}`} type="text" value={fRef} onChange={(e) => setFRef(e.target.value)} placeholder={fMethod === 'cheque' ? 'CHQ-000000' : 'TRF-000000'} maxLength={100} />
                                    {fErrors.reference_number && <p className="expl__error">{fErrors.reference_number}</p>}
                                </div>
                            )}
                            <div className="expl__field">
                                <label className="expl__label">Status</label>
                                <div className="expl__method-grid">
                                    <button type="button" className={`expl__method-btn ${fStatus === 'pending' ? 'expl__method-btn--active' : ''}`} onClick={() => setFStatus('pending')}>Pending</button>
                                    <button type="button" className={`expl__method-btn ${fStatus === 'paid' ? 'expl__method-btn--active' : ''}`} onClick={() => setFStatus('paid')}>Paid</button>
                                </div>
                                {fErrors.status && <p className="expl__error">{fErrors.status}</p>}
                            </div>
                        </div>

                        {/* Card 3 — Receipt Vault */}
                        <div className="expl__drawer-card">
                            <h4 className="expl__card-title">Receipt Vault</h4>
                            <div
                                className="expl__upload-zone"
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const files = Array.from(e.dataTransfer.files);
                                    setFReceipts((prev) => [...prev, ...files].slice(0, MAX_FILES));
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={ACCEPTED_FILE_TYPES}
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                <p className="expl__upload-text">Drag &amp; drop receipts here, or <span className="expl__upload-link">browse files</span></p>
                                <p className="expl__upload-hint">JPEG, PNG, WebP, GIF, PDF up to 10 MB each (max {MAX_FILES} files)</p>
                            </div>
                            {fReceipts.length > 0 && (
                                <div className="expl__file-list">
                                    {fReceipts.map((file, i) => (
                                        <div className="expl__file-item" key={`${file.name}-${i}`}>
                                            <span className="expl__file-name">{file.name}</span>
                                            <span className="expl__file-size">{(file.size / 1024).toFixed(0)} KB</span>
                                            <button type="button" className="expl__file-remove" onClick={() => removeFile(i)}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {fErrors.receipts && <p className="expl__error">{fErrors.receipts}</p>}
                        </div>
                    </div>

                    <div className="expl__drawer-footer">
                        <button className="expl__btn-cancel" type="button" onClick={closeDrawer}>Cancel</button>
                        <button className="expl__btn-save" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : (drawerMode === 'create' ? 'Record Expense' : 'Update Expense')}
                        </button>
                    </div>
                </form>
            </div>

            {/* Delete confirm */}
            {deleteTarget && (
                <div className="expl__overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="expl__modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="expl__modal-title">Remove Expense</h3>
                        <p className="expl__modal-text">
                            Remove <strong>{deleteTarget.voucher_number ?? deleteTarget.title}</strong>? This action can be undone by an administrator.
                        </p>
                        <div className="expl__modal-actions">
                            <button className="expl__btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="expl__btn-delete" onClick={confirmDelete} disabled={saving}>
                                {saving ? 'Removing…' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
